import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useState, useEffect } from 'react';
import { apiRawFetch } from '../../main/client';
import PropertyInviteUserDialog from './PropertyInviteUserDialog';

interface Permission {
    type: 'globalAdmin' | 'propertyAdmin';
    propertyId?: number;
}

interface Invitation {
    id: number;
    email: string;
    name: string;
    status: string;
    permissions: Permission[];
    expiresAt: string;
    invitedBy: { id: number; name: string } | null;
    createdAt: string;
    updatedAt: string;
}

interface PropertyInvitedUsersProps {
    propertyId: number;
    propertyName: string;
    disabled?: boolean;
}

const PropertyInvitedUsers = ({
    propertyId,
    propertyName,
    disabled = false,
}: PropertyInvitedUsersProps) => {
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const fetchInvitations = async () => {
        try {
            setLoading(true);
            const response = await apiRawFetch('api/invitations');

            if (!response.ok) {
                throw new Error('Failed to fetch invitations');
            }

            const data = (await response.json()) as Invitation[];
            // Filter invitations that have propertyAdmin permission for this property
            const propertyInvitations = data.filter(inv =>
                inv.permissions.some(
                    p => p.type === 'propertyAdmin' && p.propertyId === propertyId
                )
            );
            setInvitations(propertyInvitations);
        } catch {
            // Silently fail - invitations are optional
            setInvitations([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvitations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyId]);

    const handleRevokeInvitation = async (invitationId: number) => {
        try {
            const response = await apiRawFetch(
                `api/invitations/${invitationId}/revoke`,
                'POST'
            );

            if (!response.ok) {
                throw new Error('Failed to revoke invitation');
            }

            await fetchInvitations();
        } catch {
            // Silently fail
        }
    };

    const handleResendInvitation = async (invitationId: number) => {
        try {
            const response = await apiRawFetch(
                `api/invitations/${invitationId}/resend`,
                'POST'
            );

            if (!response.ok) {
                throw new Error('Failed to resend invitation');
            }

            await fetchInvitations();
        } catch {
            // Silently fail
        }
    };

    const handleInvitationSent = () => {
        setDialogOpen(false);
        fetchInvitations();
    };

    const isExpired = (expiresAt: string) => {
        return new Date(expiresAt) < new Date();
    };

    const tableCellHeaderSx = {
        fontWeight: 700,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        px: 2,
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#e5e7eb',
                    }}
                >
                    Invited Users
                </Typography>
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                    disabled={disabled}
                    sx={{
                        borderRadius: 0,
                        border: '2px solid',
                        borderColor: 'secondary.main',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 2,
                        py: 0.5,
                    }}
                >
                    Invite User
                </Button>
            </Box>

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 4,
                    }}
                >
                    <CircularProgress size={24} />
                </Box>
            ) : invitations.length === 0 ? (
                <Box
                    sx={{
                        border: '2px solid #374151',
                        p: 3,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase' }}
                    >
                        No pending invitations for this property
                    </Typography>
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        border: '2px solid #374151',
                        borderRadius: 0,
                    }}
                >
                    <Table size="small" aria-label="property invitations table">
                        <TableHead>
                            <TableRow
                                sx={{
                                    borderBottom: '3px solid #6366f1',
                                    backgroundColor: '#1a1a2e',
                                }}
                            >
                                <TableCell sx={{ ...tableCellHeaderSx, py: 1.5 }}>
                                    Email
                                </TableCell>
                                <TableCell sx={tableCellHeaderSx}>Name</TableCell>
                                <TableCell sx={tableCellHeaderSx}>Status</TableCell>
                                <TableCell align="center" sx={tableCellHeaderSx}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invitations.map(invitation => {
                                const expired = isExpired(invitation.expiresAt);
                                return (
                                    <TableRow
                                        key={invitation.id}
                                        sx={{
                                            borderBottom: '1px solid #374151',
                                            opacity: expired ? 0.5 : 1,
                                        }}
                                    >
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                            >
                                                {invitation.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Typography variant="body2">
                                                {invitation.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ px: 2 }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1.5,
                                                        py: 0.25,
                                                        border: '2px solid',
                                                        borderColor: expired
                                                            ? 'error.main'
                                                            : 'warning.main',
                                                        backgroundColor: expired
                                                            ? 'rgba(239, 68, 68, 0.1)'
                                                            : 'rgba(245, 158, 11, 0.1)',
                                                        fontWeight: 700,
                                                        fontSize: '0.625rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                        color: expired
                                                            ? 'error.main'
                                                            : 'warning.main',
                                                    }}
                                                >
                                                    {expired
                                                        ? 'Expired'
                                                        : invitation.status}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={{ px: 2 }}>
                                            {!expired && (
                                                <IconButton
                                                    onClick={() =>
                                                        handleResendInvitation(
                                                            invitation.id
                                                        )
                                                    }
                                                    size="small"
                                                    title="Resend invitation email"
                                                    disabled={disabled}
                                                    sx={{
                                                        color: 'primary.main',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(99, 102, 241, 0.1)',
                                                        },
                                                    }}
                                                >
                                                    <EmailIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                onClick={() =>
                                                    handleRevokeInvitation(
                                                        invitation.id
                                                    )
                                                }
                                                size="small"
                                                title="Revoke invitation"
                                                disabled={disabled}
                                                sx={{
                                                    color: 'error.main',
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'rgba(239, 68, 68, 0.1)',
                                                    },
                                                }}
                                            >
                                                <CancelIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <PropertyInviteUserDialog
                open={dialogOpen}
                propertyId={propertyId}
                propertyName={propertyName}
                onClose={() => setDialogOpen(false)}
                onSent={handleInvitationSent}
            />
        </Box>
    );
};

export default PropertyInvitedUsers;
